import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsSummaryResponse, FundamentalsResponse } from '@/api/types';
import { useOUs } from '@/hooks/useOUs';
import { scoreColor, fmtScore, FUND_DEFAULT_COLORS } from '@/utils/analytics';

interface FundamentalsTabProps {
  apiSummary?: AnalyticsSummaryResponse;
  apiFundamentals?: FundamentalsResponse;
  /** Estado de carga del fetch de fundamentales — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isLoadingFundamentals?: boolean;
  /** Estado de error del fetch de fundamentales — controla el estado explícito (ver MOCK_FALLBACK.md). */
  isErrorFundamentals?: boolean;
}

const DIST_COLORS = ['var(--cp-danger-light)', '#f59e0b', '#10b981', 'var(--cp-success)'];

/**
 * Normaliza un arreglo de distribución (conteos o fracciones, largo 1-4) a fracciones 0-1
 * de largo 4 (índice 0 = score 1). Si no hay datos, devuelve ceros.
 */
function normalizeDist(distribution: number[] | undefined): number[] {
  const counts = [0, 1, 2, 3].map((i) => distribution?.[i] ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  return counts.map((c) => (total > 0 ? c / total : 0));
}

function ConfBadge({ count, t }: { count: number; t: (k: string, opts?: Record<string, unknown>) => string }) {
  if (count < 3) return (
    <span style={{ fontSize: '0.6rem', padding: '1px 6px', background: '#FEF3C7', color: '#92400E', borderRadius: 4, fontWeight: 600 }}>
      {t('analytics.fundamentals.confPreliminary')}
    </span>
  );
  if (count >= 10) return (
    <span style={{ fontSize: '0.6rem', padding: '1px 6px', background: '#D1FAE5', color: '#065F46', borderRadius: 4, fontWeight: 600 }}>
      {t('analytics.fundamentals.confBasedOn', { n: count })}
    </span>
  );
  return null;
}

export function FundamentalsTab({
  apiSummary,
  apiFundamentals,
  isLoadingFundamentals = false,
  isErrorFundamentals = false,
}: FundamentalsTabProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data: ouList = [] } = useOUs();

  // Fundamentales — únicamente desde apiFundamentals; sin fallback a mock (MOCK_FALLBACK.md).
  const fundData = useMemo(() => {
    if (!apiFundamentals || apiFundamentals.length === 0) return [];
    return [...apiFundamentals]
      .sort((a, b) => b.score - a.score)
      .map((f, i) => ({
        id: f.id,
        name: f.name,
        color: f.color ?? FUND_DEFAULT_COLORS[i % FUND_DEFAULT_COLORS.length]!,
        avg: f.score,
        count: f.evaluations,
        dailyHabits: f.dailyHabits ?? [],
        distribution: f.distribution,
      }));
  }, [apiFundamentals]);

  // Mensaje de estado explícito — null cuando hay datos que mostrar.
  const fundStateMessage = useMemo(() => {
    if (isLoadingFundamentals) return t('common.loading');
    if (isErrorFundamentals) return t('common.error');
    if (fundData.length === 0) return t('analytics.summary.noOmissions');
    return null;
  }, [isLoadingFundamentals, isErrorFundamentals, fundData, t]);

  // Score general por OU (apiSummary.scoreByOu). Simplificación: el backend hoy no expone
  // desglose por fundamental+OU combinados, así que el mismo score general por OU se
  // muestra para cualquier fundamental expandido (aproximación documentada en el reporte).
  const ouScoreById = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of apiSummary?.scoreByOu ?? []) {
      map.set(s.ouId, s.score);
    }
    return map;
  }, [apiSummary]);

  return (
    <>
      <div className="au-card">
        <div style={{ marginBottom: 14 }}>
          <div className="au-card-title">
            <i className="fas fa-layer-group" aria-hidden="true" />
            {t('analytics.fundamentals.title')}
          </div>
          <div className="au-card-sub">{t('analytics.fundamentals.subtitle')}</div>
        </div>

        {fundStateMessage && (
          <div className={`au-fund-state${isErrorFundamentals ? ' au-fund-state-error' : ''}`}>
            {isLoadingFundamentals && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {isErrorFundamentals && <i className="fas fa-triangle-exclamation" aria-hidden="true" />}
            {' '}{fundStateMessage}
          </div>
        )}

        {!fundStateMessage && (
          <div className="au-fund-list">
            {fundData.map((f) => {
              const isExpanded = expanded === f.id;
              const dist = normalizeDist(f.distribution);

              return (
                <div key={f.id} className={`au-fund-item${isExpanded ? ' expanded' : ''}`}>
                  <div
                    className="au-fund-row"
                    onClick={() => setExpanded(isExpanded ? null : f.id)}
                    role="button"
                    aria-expanded={isExpanded}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setExpanded(isExpanded ? null : f.id)}
                  >
                    <div className="au-fund-color" style={{ background: f.color }} />
                    <span className="au-fund-name">{f.name}</span>
                    <div className="au-fund-bar">
                      <div
                        className="au-fund-bar-fill"
                        style={{ width: `${(f.avg / 4) * 100}%`, background: f.color }}
                      />
                    </div>
                    <span className="au-fund-score" style={{ color: scoreColor(f.avg) }}>
                      {fmtScore(f.avg)}
                    </span>
                    <span className="au-fund-count">
                      <ConfBadge count={f.count} t={t as (k: string, opts?: Record<string, unknown>) => string} />
                    </span>
                    <i className="fas fa-chevron-down au-fund-arrow" aria-hidden="true" />
                  </div>

                  {isExpanded && (
                    <div className="au-fund-detail">
                      {/* OU chips — score general por OU (ver nota de aproximación arriba) */}
                      <div className="au-fund-ou-chips">
                        {ouList.map((ou) => {
                          const score = ouScoreById.get(ou.key);
                          if (score === undefined) return null;
                          return (
                            <span key={ou.key} className="au-fund-ou-chip">
                              <strong>{ou.label}</strong>
                              <span className="score" style={{ color: scoreColor(score) }}>
                                {fmtScore(score)}
                              </span>
                            </span>
                          );
                        })}
                      </div>

                      {/* Daily habits (comportamientos) */}
                      {f.dailyHabits.map((b) => (
                        <div key={b.id} className="au-beh-item">
                          <div className="au-beh-head">
                            <span className="au-beh-name">{b.name}</span>
                            <span
                              className="au-beh-score"
                              style={{ color: b.score > 0 ? scoreColor(b.score) : undefined }}
                            >
                              {b.score > 0 ? fmtScore(b.score) : '—'}
                            </span>
                            {b.evaluations > 0 && <span className="au-beh-evals">{b.evaluations} eval</span>}
                          </div>
                          <div className="au-beh-dist">
                            {dist.map((pct, i) => (
                              <div key={i} className="au-dist-bar">
                                <span className="au-dist-level">{i + 1}</span>
                                <div className="au-dist-track">
                                  <div
                                    className="au-dist-fill"
                                    style={{ width: `${Math.round(pct * 100)}%`, background: DIST_COLORS[i] }}
                                  />
                                </div>
                                <span className="au-dist-pct">{Math.round(pct * 100)}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
